"use client";

import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="207588" className="flex gap-8">
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-4 w-20 bg-muted/10 animate-pulse rounded" />)}
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground hover:text-primary transition-colors duration-300 focus-visible:outline-none">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  closeMenu
}: {
  closeMenu: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col py-2">
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-12 w-full bg-muted/10 animate-pulse border-b border-border/10" />)}
      </div>;
  }
  return <div className="flex flex-col py-2">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} onClick={closeMenu} className="px-4 py-4 text-[10px] uppercase tracking-widest font-bold text-muted-foreground hover:text-primary hover:bg-primary/5 transition-colors border-b border-border/10 last:border-0">
          {item.label}
        </Link>)}
      <div className="px-4 pt-4 mt-2 border-t border-border/20">
        <Button asChild className="w-full font-default uppercase tracking-widest text-[10px] font-bold bg-primary text-primary-foreground shadow-[0_0_20px_rgba(59,130,246,0.3)]">
          <Link to="#" onClick={closeMenu}>Start Now</Link>
        </Button>
      </div>
    </div>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute('href');
        if (!href) return;
        if (href === '/#') {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
          return;
        }
        let hash = '';
        if (href.startsWith('#')) {
          hash = href;
        } else if (href.startsWith('/') && href.includes('#')) {
          const [path, hashPart] = href.split('#');
          if (hashPart === '') {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: 'smooth'
            });
            return;
          }
          if (path === '/' || path === window.location.pathname) {
            hash = '#' + hashPart;
          }
        }
        if (hash && hash !== '#') {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);
  return <section id="navigation" className={`dark bg-background text-foreground sticky top-0 z-50 transition-all duration-500 w-full ${isScrolled ? "bg-background/80 backdrop-blur-xl border-b border-border/30" : "bg-background"}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20 w-full">
          {}
          <div className="flex-shrink-0 flex items-center">
            <Link to="/" className="focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary rounded-sm transition-opacity hover:opacity-80">
              <img src="https://boss-stallion-1.10web.cloud/wp-content/uploads/2026/04/oceanclaw_logo-1.png" alt="OceanClaw" className="h-3.5" />
            </Link>
          </div>

          {}
          <div className="hidden md:flex flex-1 justify-center">
            <MenuProvider menu_id="16">
              <DesktopMenuConsumer />
            </MenuProvider>
          </div>

          {}
          <div className="flex items-center justify-end gap-6">
            <div className="hidden md:block">
              <Button asChild className="relative font-default uppercase tracking-widest text-[10px] font-bold bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_20px_rgba(59,130,246,0.3)] hover:shadow-[0_0_35px_rgba(59,130,246,0.5)] transition-all duration-300 border border-primary/20 rounded-lg">
                <Link to="#">Start Now</Link>
              </Button>
            </div>
            
            {}
            <div className="md:hidden flex items-center">
              <Button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} variant="ghost" size="icon" className="text-foreground hover:text-primary hover:bg-primary/5" aria-expanded={isMobileMenuOpen}>
                <span className="sr-only">Open main menu</span>
                {isMobileMenuOpen ? <X className="h-6 w-6" aria-hidden="true" /> : <Menu className="h-6 w-6" aria-hidden="true" />}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {}
      <div className={`md:hidden overflow-hidden transition-all duration-500 ease-in-out ${isMobileMenuOpen ? "max-h-[600px] opacity-100 border-b border-border/30 bg-background/98 backdrop-blur-2xl" : "max-h-0 opacity-0"}`}>
        <MenuProvider menu_id="16">
          <MobileMenuConsumer closeMenu={() => setIsMobileMenuOpen(false)} />
        </MenuProvider>
      </div>
    </section>;
}